import { forwardRef, type PropsWithChildren } from 'react'
import {
  Dialog,
  type DialogProps,
  Grow,
  type GrowProps,
  Slide,
  type SlideProps,
} from '@mui/material'
import './MixtilesDialog.scss'
import classNames from 'classnames'
import CloseButton from '../TopBar/CloseButton/CloseButton'
import useScreenSize from '../../hooks/useScreenSize'
import { useBlurActiveElementOnOpenChange } from 'utils/mui-utils'

const SlideUpTransition = forwardRef<HTMLDivElement, SlideProps>(
  function SlideUpTransition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
  }
)

const GrowTransition = forwardRef<HTMLDivElement, GrowProps>(
  function GrowTransition(props, ref) {
    return <Grow ref={ref} {...props} />
  }
)

const transitions = {
  slide: SlideUpTransition,
  grow: GrowTransition,
  noTransition: undefined,
}

const DEFAULT_DIALOG_MAX_WIDTH = 'md'

type MixtilesDialogProps = PropsWithChildren<{
  className?: string
  open: boolean
  transition?: keyof typeof transitions
  fullScreen?: boolean
  scroll?: DialogProps['scroll']
  onEntered?: () => void
  onDismiss?: () => void
  keepMounted?: boolean
  closeButtonOnDialog?: boolean
  darkCloseButton?: boolean
  maxWidth?: DialogProps['maxWidth']
  showOnTop?: boolean
  hideBackdrop?: boolean
  disableBackdropClick?: boolean
  disableEnforceFocus?: boolean
}>

function MixtilesDialog({
  className = '',
  open,
  transition = undefined,
  fullScreen = undefined,
  scroll = undefined,
  onEntered = undefined,
  onDismiss,
  keepMounted = undefined,
  children,
  closeButtonOnDialog = false,
  darkCloseButton = undefined,
  maxWidth = DEFAULT_DIALOG_MAX_WIDTH,
  showOnTop = false,
  hideBackdrop = false,
  disableBackdropClick = false,
  disableEnforceFocus = false,
}: MixtilesDialogProps) {
  const isMobile = !useScreenSize('md')
  const isFullScreen = fullScreen === undefined ? isMobile : fullScreen
  useBlurActiveElementOnOpenChange(open || false)
  const transitionComp =
    transitions[transition || (fullScreen ? 'slide' : 'grow')]
  return (
    <>
      <Dialog
        fullScreen={isFullScreen}
        scroll={scroll}
        classes={{
          paper: classNames('mixtiles-dialog-paper', className),
          paperFullScreen: 'mixtiles-dialog-paper-fullscreen',
          root: classNames('mixtiles-dialog-root', {
            'on-top': showOnTop,
            'not-full-screen': !isFullScreen,
          }),
        }}
        onClose={disableBackdropClick ? undefined : onDismiss}
        TransitionProps={{ onEntered }}
        TransitionComponent={transitionComp}
        keepMounted={keepMounted}
        maxWidth={maxWidth}
        open={open || false}
        hideBackdrop={hideBackdrop}
        disableEnforceFocus={disableEnforceFocus}
      >
        {closeButtonOnDialog ? (
          <CloseButton
            action={onDismiss}
            className="mixtiles-dialog-close-button-on-dialog"
            dark={darkCloseButton}
          />
        ) : null}
        {children}
      </Dialog>
    </>
  )
}

export default MixtilesDialog
